import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { ModernButton } from '../../components/ui/ModernButton';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import { useAuth } from '../../contexts/AuthContext';
import './LoginPage.css';

export const LoginPage: React.FC = () => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [resetEmail, setResetEmail] = useState('');
	const [showResetDialog, setShowResetDialog] = useState(false);
	const [resetMessage, setResetMessage] = useState('');
	// Password reset gets its own error and loading state so it does not render
	// behind the open dialog or spin the Sign In button
	const [resetError, setResetError] = useState('');
	const [resetLoading, setResetLoading] = useState(false);

	const { signIn, resetPassword } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	const from = location.state?.from?.pathname || '/agent/dashboard';
	const successMessage = location.state?.message;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!email || !password) {
			setError('Please enter both email and password');
			return;
		}

		setLoading(true);
		setError('');

		try {
			await signIn(email, password);
			navigate(from, { replace: true });
		} catch (error: any) {
			// Handle custom error messages or use default error handling
			const errorMessage = error.message && error.message.includes('Please contact your administrator')
				? error.message
				: getErrorMessage(error.code || error.message);
			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};


	const handleResetPassword = async () => {
		if (!resetEmail) {
			setResetError('Please enter your email address');
			return;
		}

		setResetLoading(true);
		setResetError('');

		try {
			await resetPassword(resetEmail);
			setResetMessage('Password reset email sent. Check your inbox.');
			setShowResetDialog(false);
			setResetEmail('');
		} catch (error: any) {
			setResetError(getErrorMessage(error.code));
		} finally {
			setResetLoading(false);
		}
	};

	const openResetDialog = () => {
		setResetError('');
		setResetMessage('');
		setShowResetDialog(true);
	};

	const closeResetDialog = () => {
		setShowResetDialog(false);
		setResetError('');
	};

	const getErrorMessage = (errorCode: string): string => {
		switch (errorCode) {
			case 'auth/user-not-found':
				return 'No agent account found with this email address.';
			case 'auth/wrong-password':
				return 'Incorrect password. Please try again.';
			case 'auth/invalid-email':
				return 'Please enter a valid email address.';
			case 'auth/user-disabled':
				return 'This agent account has been disabled. Please contact your administrator.';
			case 'auth/too-many-requests':
				return 'Too many failed attempts. Please try again later.';
			case 'auth/network-request-failed':
				return 'Network error. Please check your internet connection and try again.';
			case 'auth/invalid-credential':
				return 'Invalid email or password. Please check your credentials and try again.';
			case 'auth/missing-password':
				return 'Please enter your password.';
			case 'auth/weak-password':
				return 'Password is too weak. Please choose a stronger password.';
			case 'auth/email-already-in-use':
				return 'An account with this email already exists.';
			default:
				// If it's not a Firebase auth error, return the message as-is
				if (errorCode && !errorCode.startsWith('auth/')) {
					return errorCode;
				}
				return 'An error occurred during sign in. Please try again.';
		}
	};


	return (
		<>
			<div className="login-container">
				<Card className="login-card">
					<div className="login-header">
						<Link to="/" className="login-logo-link">
							<img src="/ew-color-logo_crop.png" alt="EquityWorkx" className="login-logo" />
						</Link>
						<h2>Agent Portal</h2>
						<p>Sign in to access your dashboard</p>
					</div>

				{error && <Message severity="error" text={error} className="login-error" />}
				{resetMessage && <Message severity="success" text={resetMessage} className="login-success" />}
				{successMessage && <Message severity="success" text={successMessage} className="login-success" />}

					<form onSubmit={handleSubmit} className="login-form">
						<div className="field">
							<label htmlFor="email">Email Address</label>
							<InputText
								id="email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="Enter your email"
								className="w-full"
								disabled={loading}
							/>
						</div>

						<div className="field">
							<label htmlFor="password">Password</label>
							<Password
								id="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="Enter your password"
								className="w-full"
								feedback={false}
								toggleMask
								disabled={loading}
							/>
						</div>

						<ModernButton
							type="submit"
							variant="primary"
							fullWidth
							loading={loading}
							disabled={loading}
						>
							Sign In
						</ModernButton>
					</form>

					<div className="login-footer">
						<ModernButton
							variant="ghost"
							onClick={openResetDialog}
						>
							Forgot Password?
						</ModernButton>
					</div>
				</Card>
			</div>

			{/* Password Reset Dialog */}
			<Dialog
				header="Reset Password"
				visible={showResetDialog}
				onHide={closeResetDialog}
				modal
				className="reset-dialog"
			>
				<div className="reset-content">
					<p>Enter your email address to receive a password reset link:</p>
					{resetError && <Message severity="error" text={resetError} className="login-error" />}
					<div className="field">
						<InputText
							value={resetEmail}
							onChange={(e) => setResetEmail(e.target.value)}
							placeholder="Enter your email"
							type="email"
							className="w-full"
							disabled={resetLoading}
						/>
					</div>
					<div className="modern-form-actions">
						<ModernButton
							variant="outline"
							onClick={closeResetDialog}
							disabled={resetLoading}
						>
							Cancel
						</ModernButton>
						<ModernButton
							variant="primary"
							onClick={handleResetPassword}
							loading={resetLoading}
							disabled={resetLoading}
						>
							Send Reset Link
						</ModernButton>
					</div>
				</div>
			</Dialog>
		</>
	);
}; 